import { useEffect, useRef } from 'react';

// eslint-disable-next-line max-len
import { useMSWorkTypeAssignmentListData } from 'src/hooks/assignment-list-data/use-ms-work-type-assignment-list-data.hook';
import {
  AssignmentsViewOptions,
  AssignmentsWorkTypeMap,
} from 'src/interfaces/assignment.types';
import authService from 'src/services/api/auth.service';
import {
  setActiveAssignmentsView,
  setIsNoAssignmentsDataViewMS,
  useActiveAssignmentsViewSelector,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';

import { AppsSection } from '../apps-section';
import { ColumnsHeadings } from '../columns-headings';
import { MiddleSchoolSection } from '../middle-school-section';
import { MSViewWrapper } from './ms-view-wrapper';

export const MsSections = () => {
  const dispatch = useAppDispatch();
  const activeAssignmentsView = useActiveAssignmentsViewSelector();

  // Get data from both TODO and PAST_WORK sections to manage global flag
  const { allAssignmentCount: todoCount, isLoading: isToDoAssignmentsLoading } =
    useMSWorkTypeAssignmentListData(AssignmentsWorkTypeMap.TODO);

  const { allAssignmentCount: pastWorkCount, isLoading: pastWorkLoading } =
    useMSWorkTypeAssignmentListData(AssignmentsWorkTypeMap.PAST_WORK);

  const isTeacher = authService.isTeacher();

  const prevViewRef = useRef<AssignmentsViewOptions>(
    AssignmentsViewOptions.TODO,
  );

  useEffect(() => {
    return () => {
      prevViewRef.current = activeAssignmentsView;
    };
  }, [activeAssignmentsView]);

  useEffect(() => {
    if (isTeacher || isToDoAssignmentsLoading) return;

    // Switch to the 'Apps' section instead of showing an empty 'To-Do' one.
    if (
      !todoCount &&
      prevViewRef.current === AssignmentsViewOptions.TODO &&
      activeAssignmentsView !== AssignmentsViewOptions.PAST_WORK
    ) {
      dispatch(setActiveAssignmentsView(AssignmentsViewOptions.APPS));
    }
  }, [
    isToDoAssignmentsLoading,
    activeAssignmentsView,
    dispatch,
    isTeacher,
    todoCount,
  ]);

  // Manage isNoAssignmentsDataViewMS flag based on combined data from all sections
  useEffect(() => {
    // Only set flag when both sections have loaded
    if (!isToDoAssignmentsLoading && !pastWorkLoading) {
      const totalAssignments = (todoCount ?? 0) + (pastWorkCount ?? 0);
      dispatch(setIsNoAssignmentsDataViewMS(totalAssignments === 0));
    }
  }, [
    dispatch,
    todoCount,
    pastWorkCount,
    isToDoAssignmentsLoading,
    pastWorkLoading,
  ]);

  return MSViewWrapper(
    <>
      {activeAssignmentsView === AssignmentsViewOptions.APPS ? (
        <AppsSection />
      ) : (
        <>
          <ColumnsHeadings />
          <MiddleSchoolSection section={AssignmentsWorkTypeMap.TODO} />
          <MiddleSchoolSection section={AssignmentsWorkTypeMap.PAST_WORK} />
        </>
      )}
    </>,
  );
};
